import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Newspaper, Plus, Edit, Trash2, MessageCircle, Eye, EyeOff, Users, BookOpen, Globe, AlertTriangle, Star, Tag, X, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'noticias', label: 'Noticias' },
  { value: 'anuncios', label: 'Anuncios' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'tips', label: 'Tips' },
  { value: 'capacitacion', label: 'Capacitación' }
];

const AUDIENCES = [
  { value: 'all', label: 'Todos', icon: Globe },
  { value: 'authors', label: 'Solo Autores', icon: BookOpen },
  { value: 'consumers', label: 'Solo Audio-Lectores', icon: Users }
];

const TAGS = [
  { value: 'importante', label: 'IMPORTANTE', icon: Star, color: 'bg-yellow-500' },
  { value: 'urgente', label: 'URGENTE', icon: AlertTriangle, color: 'bg-red-500' },
  { value: 'nuevo', label: 'NUEVO', icon: Tag, color: 'bg-green-500' }
];

const BlogAdmin = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    image_url: '',
    category: 'noticias',
    audience: 'all',
    tags: [],
    comments_enabled: true,
    send_email_notification: false,
    published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Error al cargar posts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      image_url: '',
      category: 'noticias',
      audience: 'all',
      tags: [],
      comments_enabled: true,
      send_email_notification: false,
      published: true
    });
    setEditingPost(null);
  };

  const handleCreate = async (e, saveAsDraft = false) => {
    e.preventDefault();
    
    const dataToSave = {
      ...formData,
      published: !saveAsDraft,
      send_email_notification: saveAsDraft ? false : formData.send_email_notification
    };
    
    try {
      if (editingPost) {
        await axios.put(
          `${API}/api/admin/blog/posts/${editingPost.id}`,
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(saveAsDraft ? 'Borrador guardado' : 'Post actualizado');
      } else {
        await axios.post(
          `${API}/api/admin/blog/posts`,
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(saveAsDraft ? 'Borrador guardado' : 'Post publicado');
      }
      
      fetchPosts();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      summary: post.summary || '',
      image_url: post.image_url || '',
      category: post.category,
      audience: post.audience,
      tags: post.tags || [],
      comments_enabled: post.comments_enabled,
      send_email_notification: false,
      published: post.published !== false
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('¿Estás seguro de eliminar este post?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/blog/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post eliminado');
      fetchPosts();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleComments = async (postId) => {
    try {
      const response = await axios.patch(
        `${API}/api/admin/blog/posts/${postId}/toggle-comments`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.comments_enabled ? 'Comentarios habilitados' : 'Comentarios deshabilitados');
      fetchPosts();
    } catch (error) {
      toast.error('Error al cambiar configuración');
    }
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAudienceIcon = (audience) => {
    const found = AUDIENCES.find(a => a.value === audience);
    return found ? found.icon : Globe;
  };

  const getAudienceLabel = (audience) => {
    const found = AUDIENCES.find(a => a.value === audience);
    return found ? found.label : audience;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="gotham-bold text-2xl text-white flex items-center gap-3">
          <Newspaper className="w-6 h-6 text-purple-400" />
          Gestión de Blog / Noticias
        </h2>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn-viubux flex items-center gap-2"
          data-testid="create-post-btn"
        >
          <Plus className="w-4 h-4" />
          Nuevo Post
        </Button>
      </div>

      {/* Posts List */}
      <Card className="card-viubux-dark border-purple-500/30">
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-white/60 text-center py-8">Cargando...</p>
          ) : posts.length === 0 ? (
            <p className="text-white/60 text-center py-8">No hay posts. Crea el primero.</p>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => {
                const AudienceIcon = getAudienceIcon(post.audience);
                return (
                  <div
                    key={post.id}
                    className="bg-black/20 rounded-xl p-4 border border-white/5"
                    data-testid={`admin-post-${post.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {/* Draft badge */}
                          {post.published === false && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-orange-500 text-white">
                              📝 BORRADOR
                            </span>
                          )}
                          {post.tags?.map((tag) => {
                            const tagInfo = TAGS.find(t => t.value === tag);
                            if (!tagInfo) return null;
                            return (
                              <span
                                key={tag}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${tagInfo.color} text-white`}
                              >
                                {tagInfo.label}
                              </span>
                            );
                          })}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            <AudienceIcon className="w-3 h-3" />
                            {getAudienceLabel(post.audience)}
                          </span>
                        </div>

                        <h3 className="gotham-bold text-lg text-white">{post.title}</h3>
                        <p className="text-white/60 text-sm mt-1 line-clamp-2">{post.summary}</p>
                        
                        <div className="flex items-center gap-4 mt-3 text-white/40 text-xs">
                          <span>{formatDate(post.created_at)}</span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.comment_count || 0} comentarios
                          </span>
                          <span className={post.comments_enabled ? 'text-green-400' : 'text-red-400'}>
                            {post.comments_enabled ? 'Comentarios ON' : 'Comentarios OFF'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleComments(post.id)}
                          className="border-white/20 text-white/60 hover:text-white"
                          title={post.comments_enabled ? 'Deshabilitar comentarios' : 'Habilitar comentarios'}
                        >
                          {post.comments_enabled ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(post)}
                          className="border-white/20 text-white/60 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="gotham-bold text-xl text-white">
                {editingPost ? 'Editar Post' : 'Nuevo Post'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                  placeholder="Título del post"
                  required
                  data-testid="post-title-input"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Contenido *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500 min-h-[200px]"
                  placeholder="Escribe el contenido del post..."
                  required
                  data-testid="post-content-input"
                />
              </div>

              {/* Summary */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Resumen (opcional)</label>
                <input
                  type="text"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                  placeholder="Resumen corto para preview"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-white/70 text-sm mb-2">URL de Imagen (opcional)</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                  placeholder="https://..."
                />
              </div>

              {/* Category & Audience */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Categoría</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                    data-testid="post-category-select"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Audiencia</label>
                  <select
                    value={formData.audience}
                    onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                    data-testid="post-audience-select"
                  >
                    {AUDIENCES.map((aud) => (
                      <option key={aud.value} value={aud.value}>{aud.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Etiquetas</label>
                <div className="flex flex-wrap gap-2">
                  {TAGS.map((tag) => {
                    const isSelected = formData.tags.includes(tag.value);
                    const IconComponent = tag.icon;
                    return (
                      <button
                        key={tag.value}
                        type="button"
                        onClick={() => toggleTag(tag.value)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                          isSelected
                            ? `${tag.color} text-white`
                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.comments_enabled}
                    onChange={(e) => setFormData({ ...formData, comments_enabled: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-white/70">Permitir comentarios</span>
                </label>

                {(formData.tags.includes('importante') || formData.tags.includes('urgente')) && (
                  <label className="flex items-center gap-3 cursor-pointer bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/30">
                    <input
                      type="checkbox"
                      checked={formData.send_email_notification}
                      onChange={(e) => setFormData({ ...formData, send_email_notification: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-yellow-200 text-sm">
                      📧 Enviar notificación por email a todos los usuarios (tag IMPORTANTE/URGENTE)
                    </span>
                  </label>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={(e) => handleCreate(e, true)}
                  className="flex-1 bg-gray-600 hover:bg-gray-500 text-white"
                  data-testid="save-draft-btn"
                >
                  💾 Guardar Borrador
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-viubux"
                  data-testid="submit-post-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {editingPost ? 'Actualizar' : 'Publicar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
